import { motion, useReducedMotion } from "framer-motion";
import { Layers, Shield, CheckCircle } from "lucide-react";
import { useTranslations } from "use-intl";
import { BrandFeature } from "./brand-feature";
import { BRAND_FEATURES } from "./brand-features";
import { STORYTELLING, type BrandPage } from "./brand-storytelling";

interface BrandPanelProps {
  page: BrandPage;
}

// 3-layer radial gradient mesh on dark base per design spec.
const BRAND_BG: React.CSSProperties = {
  background: [
    "radial-gradient(ellipse 800px 600px at 20% 10%, oklch(0.55 0.20 240) 0%, transparent 60%)",
    "radial-gradient(ellipse 600px 400px at 80% 90%, oklch(0.50 0.22 280) 0%, transparent 65%)",
    "linear-gradient(180deg, oklch(0.20 0.10 245) 0%, oklch(0.15 0.08 250) 100%)",
  ].join(", "),
  color: "white",
};

export function BrandPanel({ page }: BrandPanelProps) {
  const t = useTranslations();
  const reduceMotion = useReducedMotion();
  const story = STORYTELLING[page];

  return (
    // Hidden on mobile — form panel shows MobileBrandHeader instead.
    <aside
      className="relative hidden lg:flex flex-col justify-between overflow-hidden px-10 py-10"
      style={BRAND_BG}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.13]"
        style={{
          backgroundImage:
            "linear-gradient(oklch(1 0 0 / 0.25) 1px, transparent 1px), linear-gradient(90deg, oklch(1 0 0 / 0.25) 1px, transparent 1px)",
          backgroundSize: "42px 42px",
        }}
        aria-hidden
      />

      {/* Top: Logo + brand name */}
      <div className="flex items-center gap-3">
        <div
          className="flex size-10 items-center justify-center rounded-xl"
          style={{ background: "oklch(1 0 0 / 0.12)" }}
        >
          <Layers className="size-5 text-white" />
        </div>
        <div>
          <p className="text-base font-bold leading-tight text-white">
            Agent Brain
          </p>
          <p className="text-xs" style={{ color: "oklch(1 0 0 / 0.55)" }}>
            by NextLevelBuilder
          </p>
        </div>
      </div>

      {/* Middle: Storytelling */}
      <div className="space-y-7">
        {/* Eyebrow pill */}
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
            style={{ background: "oklch(1 0 0 / 0.12)", color: "oklch(1 0 0 / 0.80)" }}
          >
            {page === "forgot" ? (
              <Shield className="size-3" />
            ) : (
              <span
                className="size-1.5 rounded-full animate-pulse"
                style={{ background: "oklch(0.75 0.18 150)" }}
                aria-hidden
              />
            )}
            {t(story.eyebrowKey)}
          </span>
        </div>

        {/* Headline */}
        <div className="space-y-3">
          <h1 className="text-3xl font-bold leading-tight text-white">
            {t(story.headlineKey)}{" "}
            <span style={{ color: "oklch(0.75 0.18 210)" }}>
              {t(story.accentKey)}
            </span>
            {page !== "forgot" ? "." : ""}
          </h1>
          <p className="text-sm leading-relaxed" style={{ color: "oklch(1 0 0 / 0.65)" }}>
            {t(story.subKey)}
          </p>
        </div>

        {/* Features or progress checklist */}
        {story.showProgressChecklist ? (
          <ResetProgressChecklist t={t} />
        ) : (
          <motion.div
            initial={reduceMotion ? false : "hidden"}
            animate={reduceMotion ? undefined : "show"}
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.07 } },
            }}
            className="grid gap-3"
          >
            {BRAND_FEATURES.map((f, index) => (
              <BrandFeature key={f.iconName} feature={f} index={index} />
            ))}
          </motion.div>
        )}

        {/* Forgot page: extra security reassurance card */}
        {story.showReassuranceCard && (
          <div
            className="rounded-xl p-4"
            style={{ background: "oklch(1 0 0 / 0.07)", border: "1px solid oklch(1 0 0 / 0.12)" }}
          >
            <div className="flex items-center gap-2 mb-1">
              <Shield className="size-4" style={{ color: "oklch(0.75 0.18 150)" }} />
              <p className="text-sm font-semibold text-white">{t("auth.forgot.reassurance_title")}</p>
            </div>
            <p className="text-xs leading-relaxed" style={{ color: "oklch(1 0 0 / 0.60)" }}>
              {t("auth.forgot.reassurance_body")}
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        className="flex items-center justify-between text-xs"
        style={{ color: "oklch(1 0 0 / 0.40)" }}
      >
        <span>{t("auth.brand.footer")}</span>
        <div className="flex items-center gap-3">
          <a
            href="#"
            className="transition-colors hover:text-white"
            style={{ color: "oklch(1 0 0 / 0.40)" }}
          >
            {t("auth.brand.footer_privacy")}
          </a>
          <a
            href="#"
            className="transition-colors hover:text-white"
            style={{ color: "oklch(1 0 0 / 0.40)" }}
          >
            {t("auth.brand.footer_terms")}
          </a>
          <span className="flex items-center gap-1">
            <span
              className="size-1.5 rounded-full"
              style={{ background: "oklch(0.75 0.18 150)" }}
              aria-hidden
            />
            {t("auth.brand.footer_status")}
          </span>
        </div>
      </div>
    </aside>
  );
}

// Reset page replaces features with 3-step progress checklist.
function ResetProgressChecklist({ t }: { t: ReturnType<typeof useTranslations> }) {
  const steps = [
    t("auth.reset.progress_step1"),
    t("auth.reset.progress_step2"),
    t("auth.reset.progress_step3"),
  ];

  return (
    <div className="space-y-3">
      {steps.map((label, idx) => {
        // Steps 1 and 2 are complete; step 3 is in progress.
        const done = idx < 2;
        return (
          <div key={idx} className="flex items-center gap-3">
            <div
              className="flex size-7 shrink-0 items-center justify-center rounded-full"
              style={{
                background: done
                  ? "oklch(0.55 0.16 150)"
                  : "oklch(1 0 0 / 0.12)",
              }}
            >
              {done ? (
                <CheckCircle className="size-4 text-white" />
              ) : (
                <span className="size-2 rounded-full bg-white/60" />
              )}
            </div>
            <p
              className="text-sm"
              style={{ color: done ? "oklch(1 0 0 / 0.85)" : "oklch(1 0 0 / 0.50)" }}
            >
              {label}
            </p>
          </div>
        );
      })}
    </div>
  );
}
